import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import InviteService from '../../api/InviteService';
import { Invite } from '../../models/Invite';
import { toastSuccess } from '../../utils/toast';
import i18n from '../../i18n';

interface InviteState {
  link: string | null;
  invite: Invite | null;
  loading: boolean;
  error: string | null;
  joined: boolean;
}

const initialState: InviteState = {
  link: null,
  invite: null,
  loading: false,
  error: null,
  joined: false,
};

export const createInvite = createAsyncThunk(
  'invite/create',
  async (
    { saveId, roleCode }: { saveId: string; roleCode: string },
    { rejectWithValue },
  ) => {
    try {
      return await InviteService.createInvite(saveId, roleCode);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || err.message || i18n.t('auth:errors.createInvite'),
      );
    }
  },
);

export const fetchInvite = createAsyncThunk(
  'invite/fetch',
  async (token: string, { rejectWithValue }) => {
    try {
      return await InviteService.getInvite(token);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || err.message || i18n.t('auth:errors.fetchInvite'),
      );
    }
  },
);

export const acceptInvite = createAsyncThunk(
  'invite/accept',
  async (token: string, { rejectWithValue }) => {
    try {
      const data = await InviteService.acceptInvite(token);
      toastSuccess(i18n.t('auth:invite.accepted'));
      return data;
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || err.message || i18n.t('auth:errors.acceptInvite'),
      );
    }
  },
);

const inviteSlice = createSlice({
  name: 'invite',
  initialState,
  reducers: {
    resetInvite(state) {
      state.link = null;
      state.invite = null;
      state.error = null;
      state.joined = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createInvite.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.link = null;
      })
      .addCase(createInvite.fulfilled, (state, action) => {
        state.loading = false;
        state.link = action.payload as string;
      })
      .addCase(createInvite.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchInvite.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.invite = null;
      })
      .addCase(fetchInvite.fulfilled, (state, action) => {
        state.loading = false;
        state.invite = action.payload as Invite;
      })
      .addCase(fetchInvite.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(acceptInvite.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptInvite.fulfilled, (state) => {
        state.loading = false;
        state.joined = true;
      })
      .addCase(acceptInvite.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetInvite } = inviteSlice.actions;
export default inviteSlice.reducer;
